import { logger } from "@/shared/lib/logger";
import useFeedbackStore, { DIALOG_PRIORITY } from "@/shared/model/feedbackStore";

/**
 * native bridge 오류 통지 수신 (BRIDGE_PROTOCOL — AOS 가이드 §2.6 / iOS §2.13, R2).
 *
 * native 는 bridge 핸들러가 실패/거부되면 단일 전역 채널
 * `window.maketrend_onBridgeError(handler, reason)` 로 best-effort 통지한다 (핸들러별 함수 난립 회피).
 * reason 코드 집합은 양 플랫폼 공통이고, 1차 인자 handler 만 플랫폼별 식별자다 —
 * AOS 는 `window.maketrend` 메서드명(`snsLoginRequest` 등), iOS 는 message handler 명
 * (`snsLoginRequestHandler` 등). origin-drop·사용자 취소·registerDevice network 실패는 non-emit(계약).
 *
 * 미등록 시 native 호출이 `Uncaught TypeError: … is not a function` 으로 유실되어 오류가 조용히
 * 묻힌다(silent consume). 이 채널을 앱 전역 1회 등록해 (1) 모든 오류를 로깅하고, (2) SNS 로그인
 * 실패는 사용자에게 안내 팝업을 띄운다.
 *
 * native 는 evaluateJavaScript 로 전역 호출하므로 반드시 window 에 직접 할당한다
 * (ES module 지역 함수는 native 가 찾지 못함 — globalSnsLoginCallback 과 동일 패턴).
 */

// SNS 로그인 진입점의 handler 식별자 (AOS 메서드명 / iOS message handler 명).
const SNS_LOGIN_HANDLERS = new Set(["snsLoginRequest", "snsLoginRequestHandler"]);

export const registerBridgeErrorHandler = (): (() => void) => {
  const handler: NonNullable<Window["maketrend_onBridgeError"]> = (bridgeHandler, reason) => {
    // reason 은 고정 코드 문자열(토큰·PII 미포함, 계약). 항상 로깅해 silent consume 을 제거한다.
    logger.warn("[bridge] error", { handler: bridgeHandler, reason });

    // SNS 로그인 실패는 사용자 조치 대상 — reason 세부와 무관하게 실패 안내 팝업.
    if (SNS_LOGIN_HANDLERS.has(bridgeHandler)) {
      useFeedbackStore.getState().openAlertDialog({
        title: "로그인에 실패했어요",
        message: "잠시 후 다시 시도해 주세요.",
        priority: DIALOG_PRIORITY.NORMAL,
      });
    }
  };

  window.maketrend_onBridgeError = handler;

  return () => {
    // 등록 이후 다른 곳에서 덮어쓰지 않았을 때만 정리한다.
    if (window.maketrend_onBridgeError === handler) {
      delete window.maketrend_onBridgeError;
    }
  };
};
